import { trackClientEvent } from 'analytics/analytics'
import CompareOffersToggle from 'components/Common/CompareOffersToggle'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import FilterPanelRadioGroup from 'components/Common/FilterPanel/FilterPanelRadioGroup'
import {
  filterItemsToOptions,
  generateOptionItems,
  hasNoAvailableFilters,
  sortPopularItems,
  sortRecentItems,
} from 'components/OfferList/OfferListFilter/OfferListFilterUtils'
import MapWindow from 'components/Search/MapWindow'
import type { EnabledFilters, RecentFilters } from 'components/Search/type'
import {
  getAgentHubExclusiveFilterDisplayName,
  getAppliedFilterCount,
  getLuxPlusFilterDisplayName,
  getRecentFilters,
  isAgentHubExclusiveFilter,
  isOfTypeLoyaltyFeatureFilter,
  isOfTypeLuxPlusFeatureFilter,
  saveRecentFilters,
  getIsEnabledFilterKey,
} from 'components/Search/utils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import config from 'constants/config'
import type { OfferListSortOption } from 'constants/offerListFilters'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import {
  ANYWHERE_PLACE_ID,
  CUSTOMER_RATING_RESPONSE_KEY_BY_VALUE,
  LIMITED_TIME_LUX_EXCLUSIVE,
} from 'constants/search'
import useCustomerRatingOptions from 'hooks/Search/useCustomerRatingOptions'
import useLoyaltyFilterLabels from 'hooks/Search/useLoyaltyFilterLabels'
import useSearchLabels from 'hooks/Search/useSearchLabels'
import { queryKeyCampaigns, queryKeyFilters, queryKeyPages, chainsDelimiter } from 'constants/url'
import { getZeroResultFilterUrlUpdates } from './utils/zeroResultFilterCleanup'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import { EmptyArray, isNonEmptyArray, take, unique } from 'lib/array/arrayUtils'
import { isNumber } from 'lib/maths/mathUtils'
import { occupancyHasChildren } from 'lib/offer/occupancyUtils'
import getObjectKey from 'lib/object/getObjectKey'
import {
  deleteSearchParams,
  setManySearchParamValues,
  setSearchParamValue,
  toggleSearchParamValue,
} from 'lib/url/searchUrlUtils'
import debounce from 'lodash/debounce'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { rem } from 'polished'
import type { ChangeEventHandler } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addHistoricalAction } from 'actions/SystemActions'
import { selectLoggedIn } from 'selectors/accountSelectors'
import HotelSearchAddDestinationToggle from './HotelAddDestinationToggle/HotelSearchAddDestinationToggle'
import { NewBadge } from './HotelSearchFilterNewBadge'
import HotelSearchFilterPaneLoadingSkeleton from './HotelSearchFilterPaneLoadingSkeleton'
import HotelSearchSocieteFilters from './HotelSearchSocieteFilters'
import RoomFilterGroup from './RoomFilterGroup'
import BedroomsRadioFilterGroup from './BedroomsRadioFilterGroup'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import { LOYALTY_SEARCH_FILTER_PARAM } from 'loyalty/constants/loyaltySearchFilters'
import { updateBedroomsRadioFilterSearch, updateRoomFilterSearch } from './utils/updateRoomFilterSearch'
import { isStandardOccupancy } from 'lib/search/searchUtils'
import {
  getSearchVerticalAvailabilityFilters,
  getSearchVerticalTabOptions,
  isHomePropertyType,
  isHomesSearchVertical,
  isHotelsSearchVertical,
} from './searchVerticalTabsUtils'
import useHomesAndVillasToggleInSearchExperiment from './useHomesAndVillasToggleInSearchExperiment'
import { isHotelSearchResultsPath } from './utils/hotelFilterSectionsV2'
import { triplePointCampaignId, triplePointsTakeoverId } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeover.constants'
import { LUX_PLUS_MONTH_CAMPAIGN_ID, LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import LuxPlusMonthTakeoverFilterLabel from 'takeovers/luxPlusMonthTakeover/components/LuxPlusMonthTakeoverFilterLabel'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import type { FilterControlChangeHandler } from './HotelSearchPageFilterPane.types'

export type { FilterControlChangeHandler } from './HotelSearchPageFilterPane.types'

type OfferListFilterType = Exclude<keyof App.OfferListAvailableFilters, 'total'>
const newOptions = new Set(['homes_villas', 'solo_traveler'])
const DEBOUNCE_DELAY = 500

type TreatmentFilterSection =
  | 'accommodationType'
  | 'inclusions'
  | 'luxPlus'
  | 'societe'
  | 'propertyFacilities'
  | 'roomAmenities'
  | 'customerRating'
  | 'holidayTypes'
  | 'bedrooms'
  | 'bedType'
  | 'propertyBrand'

/** Treatment pane section order when occupancy has no children. */
const TREATMENT_FILTER_SECTION_ORDER_DEFAULT: Array<TreatmentFilterSection> = [
  'inclusions',
  'luxPlus',
  'societe',
  'holidayTypes',
  'customerRating',
  'accommodationType',
  'roomAmenities',
  'propertyFacilities',
  'bedrooms',
  'bedType',
  'propertyBrand',
]

/** Treatment pane section order when occupancy includes children/infants. */
const TREATMENT_FILTER_SECTION_ORDER_WITH_CHILDREN: Array<TreatmentFilterSection> = [
  'inclusions',
  'luxPlus',
  'societe',
  'accommodationType',
  'customerRating',
  'holidayTypes',
  'bedrooms',
  'roomAmenities',
  'propertyFacilities',
  'bedType',
  'propertyBrand',
]

const InputGroup = styled(Group)`
  margin-top: ${rem(16)};
`

const StyledBodyText = styled(BodyTextBlock)`
  margin-top: ${rem(8)};
`

const StyledAddDestinationToggle = styled(HotelSearchAddDestinationToggle)`
  border-bottom: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  padding-bottom: ${rem(8)};
`

const FilterPaneContainer = styled.div<{ $applyZoomCompensation?: boolean }>`
  display: flex;
  flex-direction: column;
  grid-area: offer-filters;
  position: sticky;
  top: 0;
  padding-top: ${rem(20)};

  ${mediaQueryUp.desktop} {
    height: calc(var(--vh) * 100);
    padding-inline: 0;
  }

  ${props => props.$applyZoomCompensation && `
    min-height: 165vh;
  `}
`

const StickyHeader = styled(Group)`
  padding-bottom: ${rem(8)};
`

const ScrollableContent = styled.div`
  overflow-y: auto;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */

  ${mediaQueryUp.desktop} {
    /* To accommodate the offer comparison footer */
    padding-bottom: ${rem(100)};
  }
`

const MobileComparisonToggleWrapper = styled.div`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  padding-block: ${rem(16)};
`

interface MappedStateProps {
  fetchingPopularFilters: boolean
  popularFilters: App.PopularFilters
  pathname: string
  currentHash: string
  canViewLuxPlusBenefits: boolean
  userId?: string
  isLoggedIn: boolean
}

interface Props {
  filters: App.OfferListFilters
  mapViewURL: string
  search: string
  enabledFilters: EnabledFilters
  fetching?: boolean
  mainListFetching?: boolean
  sortOptions?: Array<OfferListSortOption>
  hotelOfferList?: App.OfferList
  overrideAvailableFilters?: App.OfferListFilterOptions
  doNotScrollToTopOfSelection?: boolean
  showLoadingSkeletonOnEachFilterSelection?: boolean
  bedroomsFilterGroupAtBottom?: boolean
  onFilterChange: FilterControlChangeHandler
  onClose?: () => void
  hideCompareOffersToggle?: boolean
  hideAddDestinationToggle?: boolean
  showNoFiltersAvailable?: boolean
  applyZoomCompensation?: boolean
  showRoomsAndAmenitiesAtTop?: boolean
  enableSearchVerticalTabsExperiment?: boolean
  // When the pane is embedded outside the search results page (e.g. the destination product page),
  // a filter change triggers a full navigation to /search. In that context the automatic
  // zero-result cleanup must not run, otherwise it navigates the user away on mount.
  disableZeroResultFilterCleanup?: boolean
}

// Custom hook to track filter changes and dispatch historical actions
// Tracks "ever selected in session" and current active state
// Initialize to empty so URL filters are treated as "new" on first render (rehydration)
function useFilterChangeTracking(
  filterType: 'inclusions' | 'amenities',
  currentFilters: Array<string> | undefined,
  dispatch: ReturnType<typeof useAppDispatch>,
) {
  const previousFiltersRef = useRef<Array<string>>(EmptyArray)

  useEffect(() => {
    const previousFilters = previousFiltersRef.current
    const currentFiltersArray = currentFilters ?? EmptyArray

    // Find newly checked filters
    const newlyCheckedFilters = currentFiltersArray.filter(
      filter => !previousFilters.includes(filter),
    )

    // Find newly unchecked filters
    const newlyUncheckedFilters = previousFilters.filter(
      filter => !currentFiltersArray.includes(filter),
    )

    // Dispatch active: true for newly checked
    newlyCheckedFilters.forEach((filter) => {
      dispatch(addHistoricalAction('filter_selected', {
        filterType,
        value: filter,
        active: true,
      }))
    })

    // Dispatch active: false for newly unchecked
    newlyUncheckedFilters.forEach((filter) => {
      dispatch(addHistoricalAction('filter_selected', {
        filterType,
        value: filter,
        active: false,
      }))
    })

    previousFiltersRef.current = currentFiltersArray
  }, [currentFilters, dispatch, filterType])
}

function HotelSearchPageFilterPane(props: Props & MappedStateProps) {
  const {
    filters,
    fetching,
    mapViewURL,
    sortOptions = EmptyArray,
    search,
    enabledFilters,
    popularFilters,
    fetchingPopularFilters,
    pathname,
    currentHash,
    onFilterChange,
    onClose,
    canViewLuxPlusBenefits,
    isLoggedIn,
    userId,
    overrideAvailableFilters,
    mainListFetching,
    doNotScrollToTopOfSelection = false,
    showLoadingSkeletonOnEachFilterSelection = false,
    bedroomsFilterGroupAtBottom = false,
    hideCompareOffersToggle = false,
    hideAddDestinationToggle = false,
    showNoFiltersAvailable = false,
    applyZoomCompensation = false,
    showRoomsAndAmenitiesAtTop = false,
    enableSearchVerticalTabsExperiment = false,
    disableZeroResultFilterCleanup = false,
  } = props
  const scrollableContentRef = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const isMobileOrTablet = useIsMobileOrTablet()
  const isHotelSearchResultsPage = isHotelSearchResultsPath(pathname)
  const hideSocieteFilters = isMobileOrTablet
  const { SORT_OPTION_RECOMMENDED } = useOfferListSortOptions()
  const { CUSTOMER_RATING_GTE, CUSTOMER_RATING_GTE_BY_VALUE } = useCustomerRatingOptions()
  const { limitedTimeLuxExclusive } = useSearchLabels()
  const { getLabel: getLoyaltyFilterLabel } = useLoyaltyFilterLabels()
  const currentTakeover = useCurrentSiteTakeover({ vertical: 'hotel' })

  const filtersHeading = intl.formatMessage({
    id: 'oOtxgVWtqoiio1YzzqkB',
    defaultMessage: 'Filters',
    description: 'Heading for the filter pane on search page in the search interface',
  })
  const sortAndFiltersHeading = intl.formatMessage({
    id: 'NN2kutDlwidQq_pNtcfy',
    defaultMessage: 'Sort & filters',
    description: 'Mobile heading for sort and filters pane on search page',
  })
  const sortByTitle = intl.formatMessage({
    id: 'gmh0miF1EG1G1HaHJx8K',
    defaultMessage: 'Sort by',
    description: 'Title for sort options group on search page in the search interface',
  })
  const noFiltersAvailableText = intl.formatMessage({
    id: 'HUzmPYnG8Qs-S8m8iKC8',
    defaultMessage: 'No filters available',
    description: 'Message shown when no filters are available on search page',
  })
  const recentFiltersTitle = intl.formatMessage({
    id: 'V_cvHNSqCNqYCnh235p5',
    defaultMessage: 'Recent filters',
    description: 'Heading title for recent filters group on the search page',
  })
  const popularFiltersTitle = intl.formatMessage({
    id: 'NAsQyB5EhYsAKCEk3iRE',
    defaultMessage: 'Popular filters',
    description: 'Heading title for popular filters group on the search page',
  })
  const customerRatingTitle = intl.formatMessage({
    id: 'Kl7pgMk-5JhbPOyy7E4e',
    defaultMessage: 'Customer rating',
    description: 'Title for customer rating filter group on search page',
  })
  const holidayTypeTitle = intl.formatMessage({
    id: '6zL0mfztDFpmoqBdhxpA',
    defaultMessage: 'Type of escape',
    description: 'Label for holiday types filter dropdown in the search or listing filters panel',
  })
  const propertyTypeTitle = intl.formatMessage({
    id: 'J9qHVj4LiSKWqIYQmi19',
    defaultMessage: 'Accommodation type',
    description: 'Title for property type filter group on search page',
  })
  const inclusionsTitle = intl.formatMessage({
    id: 'MRkUK8Yn4LEC6Ws_tkF7',
    defaultMessage: 'Inclusions',
    description: 'Heading title for inclusions filter group on the search page',
  })
  const amenitiesTitle = intl.formatMessage({
    id: 'YornE0030EkWkqAvYhbw',
    defaultMessage: 'Amenities',
    description: 'Heading title for amenities filter group on the search page',
  })
  const propertyFacilitiesTitle = intl.formatMessage({
    id: 'i83EFc1CzgGeHMfYe8YZ',
    defaultMessage: 'Property Facilities',
    description: 'Heading title for property facilities filter group on the hotel search filter pane',
  })
  const roomAmenitiesTitle = intl.formatMessage({
    id: '6AWekOyQsD1mg-y_J4jo',
    defaultMessage: 'Room Amenities',
    description: 'Heading title for room amenities filter group on the hotel search filter pane',
  })
  const bedTypeTitle = intl.formatMessage({
    id: 'T-NyNqj5CIxtwwVtv1hN',
    defaultMessage: 'Bed Type',
    description: 'Heading title for bed type filter group on the hotel search filter pane',
  })
  const paymentOptionsTitle = intl.formatMessage({
    id: 'TrTAW1ZiSr8-mYCZMutT',
    defaultMessage: 'Payment options',
    description: 'Title for payment options filter group on search page',
  })
  const propertyBrandsTitle = intl.formatMessage({
    id: 'Bti3XMtVrJLnG0LpoetS',
    defaultMessage: 'Property brands',
    description: 'Title for property brands filter group on search page',
  })
  const agentHubFeaturesTitle = intl.formatMessage(
    {
      id: 'mpE2YhOBk81tdtDXN2K7',
      defaultMessage: '{displayName} Features',
      description: 'Title for agent hub features filter group on search page',
    },
    { displayName: agentHubOrSubBrandConfig.displayShortName },
  )
  const closeLabel = intl.formatMessage({
    id: 'M8NWF3WiZsj9O3wRx3Lm',
    defaultMessage: 'close',
    description: 'Aria label for close button on filter pane in the filters panel',
  })

  const searchParams = useMemo(() => {
    const params = new URLSearchParams(search)
    params.delete(queryKeyPages)
    return params.toString()
  }, [search])

  const [localAvailableFilters, setLocalAvailableFilters] = useState<App.OfferListFilterOptions | undefined>()
  const recentFilters = getRecentFilters()
  const getFilterDisplayName = useCallback((key: string, filter: string): string => {
    if (key === 'luxPlusFeatures') {
      return getLuxPlusFilterDisplayName(intl, filter)
    }

    if (key === 'loyalty') {
      return getLoyaltyFilterLabel(filter)
    }

    if (key === queryKeyCampaigns && (filter === triplePointCampaignId || filter === LUX_PLUS_MONTH_CAMPAIGN_ID)) {
      return getLoyaltyFilterLabel(filter)
    }

    if (key === 'agentHubFeatures') {
      return getAgentHubExclusiveFilterDisplayName(intl, filter)
    }

    const filterArray = localAvailableFilters?.filters?.[key as OfferListFilterType]
    if (Array.isArray(filterArray)) {
      const item = filterArray.find(item => item.slug === filter)
      if (item) return item.label
    }

    return filter
  }, [intl, getLoyaltyFilterLabel, localAvailableFilters])
  const isMapPage = pathname.includes('/map')
  const fetchedAvailableFilters = useOfferListFilters(filters)
  const availableFilters = overrideAvailableFilters ?? fetchedAvailableFilters
  const canEvaluateSearchVerticalTabsExperiment = enableSearchVerticalTabsExperiment && !!enabledFilters.propertyTypes
  const homesAndVillasToggleExperiment = useHomesAndVillasToggleInSearchExperiment({
    canActivateExperiment: false,
    canShowToggle: canEvaluateSearchVerticalTabsExperiment,
    destinationId: filters.destinationId,
    destinationIds: filters.destinationIds,
    destinationName: filters.destinationName,
    rooms: filters.rooms,
  })
  const searchVerticalAvailabilityFilters = useMemo(() => (
    getSearchVerticalAvailabilityFilters(filters)
  ), [filters])
  const searchVerticalAvailabilityFilterOptions = useMemo(() => ({
    disabled: !homesAndVillasToggleExperiment.shouldShowToggle,
    noFetch: true,
  }), [homesAndVillasToggleExperiment.shouldShowToggle])
  const searchVerticalAvailableFilters = useOfferListFilters(searchVerticalAvailabilityFilters, searchVerticalAvailabilityFilterOptions)
  const searchVerticalAvailablePropertyTypes = searchVerticalAvailableFilters.filters?.propertyTypes ?? EmptyArray

  useEffect(() => {
    if (!showLoadingSkeletonOnEachFilterSelection) {
      setLocalAvailableFilters(availableFilters)
      return
    }

    // do not set the new filters until they have finished loading
    if (!availableFilters.fetching && !mainListFetching) {
      setLocalAvailableFilters(availableFilters)
    }
  }, [availableFilters, filters.propertyId, mainListFetching, showLoadingSkeletonOnEachFilterSelection])

  const topLevelSearchKey = getObjectKey({
    propertyId: filters.propertyId,
    destinationId: filters.destinationId,
    landmarkId: filters.landmarkId,
    checkIn: filters.checkIn,
    checkOut: filters.checkOut,
    flexibleMonths: filters.flexibleMonths,
    flexibleNights: filters.flexibleNights,
    rooms: filters.rooms,
    query: filters.query,
  })

  // Reset existing filters when top-level search parameters change (destination, dates, rooms)
  useEffect(() => {
    if (!showLoadingSkeletonOnEachFilterSelection) return

    if (availableFilters.fetching) {
      setLocalAvailableFilters(undefined)
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [topLevelSearchKey])

  const isAnywhereSearch = filters.destinationId === ANYWHERE_PLACE_ID
  const searchVerticalTabOptions = useMemo(() => (
    getSearchVerticalTabOptions(filters.propertyTypes, searchVerticalAvailablePropertyTypes)
  ), [filters.propertyTypes, searchVerticalAvailablePropertyTypes])
  const isSearchVerticalTabsEnabled = homesAndVillasToggleExperiment.shouldShowToggle && searchVerticalTabOptions.length >= 2
  const isHotelsVerticalSelected = isSearchVerticalTabsEnabled && isHotelsSearchVertical(filters.propertyTypes)
  const isHomesVerticalSelected = isSearchVerticalTabsEnabled && isHomesSearchVertical(filters.propertyTypes)
  const showAccommodationTypeAtTop = !isStandardOccupancy(filters) || isSearchVerticalTabsEnabled
  const showLoadingSkeletons = showLoadingSkeletonOnEachFilterSelection ? !localAvailableFilters : availableFilters.fetching
  const sortByChecked = true
  const hideCounts = true

  const customerRatingItems = useMemo(
    () => {
      const ratingsArray = localAvailableFilters?.filters?.customerRatings ?? []
      const getCountBySlug = (slug: string) => ratingsArray.find(item => item.slug === slug)?.count

      const items = [
        {
          value: CUSTOMER_RATING_GTE.ANY_RATING.value,
          label: CUSTOMER_RATING_GTE.ANY_RATING.label,
        },
        {
          value: CUSTOMER_RATING_GTE.EXCEPTIONAL.value,
          label: <Group direction="horizontal" horizontalAlign="space-between">
            {CUSTOMER_RATING_GTE.EXCEPTIONAL.label}
          </Group>,
          count: getCountBySlug(CUSTOMER_RATING_RESPONSE_KEY_BY_VALUE[CUSTOMER_RATING_GTE.EXCEPTIONAL.value]),
          hideCount: hideCounts,
        },
        {
          value: CUSTOMER_RATING_GTE.VERY_GOOD.value,
          label: <Group direction="horizontal" horizontalAlign="space-between">
            {CUSTOMER_RATING_GTE.VERY_GOOD.label}
          </Group>,
          count: getCountBySlug(CUSTOMER_RATING_RESPONSE_KEY_BY_VALUE[CUSTOMER_RATING_GTE.VERY_GOOD.value]),
          hideCount: hideCounts,
        },
        {
          value: CUSTOMER_RATING_GTE.GOOD.value,
          label: <Group direction="horizontal" horizontalAlign="space-between">
            {CUSTOMER_RATING_GTE.GOOD.label}
          </Group>,
          count: getCountBySlug(CUSTOMER_RATING_RESPONSE_KEY_BY_VALUE[CUSTOMER_RATING_GTE.GOOD.value]),
          hideCount: hideCounts,
        },
      ]
      return items.filter((item) => item.value === CUSTOMER_RATING_GTE.ANY_RATING.value || (item.count && item.count > 0))
    }, [localAvailableFilters?.filters?.customerRatings, hideCounts, CUSTOMER_RATING_GTE])

  const { holidayTypeItems, propertyTypeItems, amenityItems, propertyFacilityItems, roomAmenityItems, bedTypeItems, inclusionItems, luxPlusFeatureItems, loyaltyFeatureItems, agentHubFeatureItems, paymentOptionItems, chainItems } = useMemo(() => {
    let luxPlusFilters = localAvailableFilters?.filters?.luxPlusFeatures ?? []
    if (!canViewLuxPlusBenefits) {
      luxPlusFilters = luxPlusFilters.filter(item => item.slug !== 'member_exclusive')
    }

    // TODO: Removable after campaign is over. Set societeFeatureFilters to be only the local loyalty filters
    const isTriplePointsTakeoverActive = currentTakeover?.id === triplePointsTakeoverId
    const isLuxPlusMonthTakeoverActive = currentTakeover?.id === LUX_PLUS_MONTH_TAKEOVER_ID
    const loyaltyFilters = localAvailableFilters?.filters?.loyalty ?? []
    const societeFeatureFilters: Array<App.FilterItem> = [
      ...loyaltyFilters,
      ...(isTriplePointsTakeoverActive ? [{
        slug: triplePointCampaignId,
        label: getLoyaltyFilterLabel(triplePointCampaignId),
        count: 0,
      }] : []),
    ]

    const loyaltyFeatureItems = generateOptionItems(
      filterItemsToOptions(societeFeatureFilters), new Set([
        ...(filters.loyalty ?? EmptyArray),
        ...(filters.campaigns ?? EmptyArray),
      ]), { sortByChecked, hideCounts },
    ).map(item => ({
      ...item,
      label: getFilterDisplayName('loyalty', item.value as string),
    }))

    return {
      holidayTypeItems: generateOptionItems(filterItemsToOptions((localAvailableFilters?.filters?.holidayTypes ?? []).filter(f => f.count > 0), localAvailableFilters?.filterOrder?.holidayTypes ?? {}), new Set(filters.holidayTypes), { sortByChecked, hideCounts, newOptions }),
      propertyTypeItems: generateOptionItems(filterItemsToOptions((localAvailableFilters?.filters?.propertyTypes ?? []).filter(f => f.count > 0), localAvailableFilters?.filterOrder?.propertyTypes ?? {}), new Set(filters.propertyTypes), { sortByChecked, hideCounts }),
      amenityItems: generateOptionItems(filterItemsToOptions((localAvailableFilters?.filters?.amenities ?? []).filter(f => f.count > 0), localAvailableFilters?.filterOrder?.amenities ?? {}), new Set(filters.amenities), { sortByChecked, hideCounts }),
      propertyFacilityItems: generateOptionItems(filterItemsToOptions((localAvailableFilters?.filters?.propertyFacilities ?? []).filter(f => f.count > 0), localAvailableFilters?.filterOrder?.propertyFacilities ?? {}), new Set(filters.propertyFacilities), { sortByChecked, hideCounts }),
      roomAmenityItems: generateOptionItems(filterItemsToOptions((localAvailableFilters?.filters?.roomAmenities ?? []).filter(f => f.count > 0), localAvailableFilters?.filterOrder?.roomAmenities ?? {}), new Set(filters.roomAmenities), { sortByChecked, hideCounts }),
      bedTypeItems: generateOptionItems(filterItemsToOptions((localAvailableFilters?.filters?.bedTypes ?? []).filter(f => f.count > 0), localAvailableFilters?.filterOrder?.bedTypes ?? {}), new Set(filters.bedTypes), { sortByChecked, hideCounts }),
      luxPlusFeatureItems: [
        ...(isLuxPlusMonthTakeoverActive ? [{
          value: LUX_PLUS_MONTH_CAMPAIGN_ID,
          label: <LuxPlusMonthTakeoverFilterLabel />,
          checked: !!filters.campaigns?.includes(LUX_PLUS_MONTH_CAMPAIGN_ID),
          count: 0,
          hideCount: hideCounts,
        }] : []),
        ...generateOptionItems(filterItemsToOptions(luxPlusFilters.filter(f => f.count > 0)), new Set(filters.luxPlusFeatures), { sortByChecked, hideCounts }),
      ],
      loyaltyFeatureItems,
      inclusionItems: generateOptionItems(filterItemsToOptions((localAvailableFilters?.filters?.inclusions ?? []).filter(f => f.count > 0), localAvailableFilters?.filterOrder?.inclusions ?? {}), new Set(filters.inclusions), { sortByChecked, hideCounts }),
      agentHubFeatureItems: generateOptionItems(filterItemsToOptions((localAvailableFilters?.filters?.agentHubFeatures ?? []).filter(f => f.count > 0)), new Set(filters.agentHubFeatures), { sortByChecked, hideCounts }),
      paymentOptionItems: generateOptionItems(filterItemsToOptions((localAvailableFilters?.filters?.paymentOptions ?? []).filter(f => f.count > 0)), new Set(filters.paymentOptions), { sortByChecked, hideCounts }),
      chainItems: generateOptionItems(filterItemsToOptions((localAvailableFilters?.filters?.chains ?? []).filter(f => f.count > 0)), new Set(filters.chains), { sortByChecked, hideCounts }),
    }
  }, [localAvailableFilters, canViewLuxPlusBenefits, filters, sortByChecked, hideCounts, getFilterDisplayName, currentTakeover?.id, getLoyaltyFilterLabel])

  // Track filter changes for inclusions and amenities
  useFilterChangeTracking('inclusions', filters.inclusions, dispatch)
  useFilterChangeTracking('amenities', filters.amenities, dispatch)

  // Auto-remove selected filter values that have dropped to 0 results in the current context.
  // Prevents the user being stuck on a 0-result page when other active filters make a
  // previously-selected value incompatible.
  useEffect(() => {
    if (disableZeroResultFilterCleanup) return
    if (!localAvailableFilters?.filters) return
    const updates = getZeroResultFilterUrlUpdates(localAvailableFilters.filters, filters)
    if (!updates.length) return
    onFilterChange({ search: setManySearchParamValues(searchParams, updates), hash: currentHash })
  }, [disableZeroResultFilterCleanup, localAvailableFilters?.filters, filters, searchParams, onFilterChange, currentHash])
  const accommodationTypeItems = useMemo(() => {
    if (!isHomesVerticalSelected) return propertyTypeItems

    return propertyTypeItems.filter(item => isHomePropertyType(item.value as string))
  }, [isHomesVerticalSelected, propertyTypeItems])

  const hasCustomerRatingOptions = customerRatingItems.some(
    item => item.value !== CUSTOMER_RATING_GTE.ANY_RATING.value,
  )

  const itemifyFilterGroup = useCallback((filterGroup: App.PopularFilters | RecentFilters) => {
    const getFilterCount = (key: string, slug: string): number | undefined => {
      const filterArray = localAvailableFilters?.filters?.[key as OfferListFilterType]
      if (Array.isArray(filterArray)) {
        return filterArray.find(item => item.slug === slug)?.count
      }
      return undefined
    }

    const isFilterChecked = (filterGroupKey: string, filter: string) => {
      if (filterGroupKey in filters) {
        const filterGroup = filters[filterGroupKey as keyof App.OfferListFilters]
        if (Array.isArray(filterGroup) && filterGroup.length > 0) {
          return (filterGroup as Array<string>).includes(filter)
        }
      }
      return false
    }

    const items = Object.entries(filterGroup).map(([key, value]: [string, Array<string> | number]) => {
      if (key === 'customerRatingGte' && 'customerRatingGte' in filterGroup && isNumber(value)) {
        const customerRating = filterGroup.customerRatingGte ? CUSTOMER_RATING_GTE_BY_VALUE[filterGroup.customerRatingGte] : undefined
        const ratingsArray = localAvailableFilters?.filters?.customerRatings ?? []
        return {
          value,
          label: customerRating?.label,
          count: ratingsArray.find(item => item.slug === CUSTOMER_RATING_RESPONSE_KEY_BY_VALUE[value])?.count,
          checked: filters.customerRatingGte === value,
          enabled: enabledFilters.customerRating,
          hideCount: hideCounts,
        }
      }
      if (isNumber(value) || key === 'orderBy' || key === 'offerTypes' || (key === 'locations' && !isAnywhereSearch)) {
        return
      }
      return unique(value).map((filter) => ({
        value: filter,
        label: <div>
          <span>{filter === LUX_PLUS_MONTH_CAMPAIGN_ID ? <LuxPlusMonthTakeoverFilterLabel /> : getFilterDisplayName(key, filter)}</span>
          {newOptions.has(filter) && <NewBadge>{intl.formatMessage({
            id: 'mZLAt9872vTo1eXKxeP5',
            defaultMessage: 'NEW',
            description: 'Badge label for new filter options on hotel search page',
          })}
          </NewBadge>}
        </div>,
        count: getFilterCount(key, filter),
        checked: isFilterChecked(key, filter),
        enabled: getIsEnabledFilterKey(key) && enabledFilters[key],
        hideCount: hideCounts,
      }))
    }).flat()
    return items.filter(Boolean) as Array<FilterPanelCheckItem>
  }, [filters, isAnywhereSearch, localAvailableFilters, enabledFilters, hideCounts, intl, CUSTOMER_RATING_GTE_BY_VALUE, getFilterDisplayName])

  const recentFilterItems = useMemo(() => {
    const isTriplePointsTakeoverActive = currentTakeover?.id === triplePointsTakeoverId
    const isLuxPlusMonthTakeoverActive = currentTakeover?.id === LUX_PLUS_MONTH_TAKEOVER_ID
    const filteredSortedRecentItems = itemifyFilterGroup(recentFilters)
      .filter(item => (
        item?.enabled && ((item.count && item.count > 0) || item.checked)
      ) || (
        item.value === triplePointCampaignId && isTriplePointsTakeoverActive
      ) || (
        item.value === LUX_PLUS_MONTH_CAMPAIGN_ID && isLuxPlusMonthTakeoverActive
      ))
      .sort((a, b) => sortRecentItems(a, b, recentFilters?.orderBy ?? []))
    return take(filteredSortedRecentItems, 10)
  }, [recentFilters, itemifyFilterGroup, currentTakeover?.id])

  const popularFilterItems = useMemo(() => {
    const filteredSortedPopularItems = itemifyFilterGroup(popularFilters)
      .filter(item => item?.count && item.count > 0)
      .sort((a, b) => sortPopularItems(a, b, popularFilters.orderBy, newOptions))

    const luxExclusiveFilter = localAvailableFilters?.filters?.holidayTypes?.find(item => item.slug === LIMITED_TIME_LUX_EXCLUSIVE)
    if (isMapPage && luxExclusiveFilter) {
      filteredSortedPopularItems.unshift({
        value: LIMITED_TIME_LUX_EXCLUSIVE,
        label: limitedTimeLuxExclusive,
        count: luxExclusiveFilter.count,
        checked: filters.holidayTypes?.includes(LIMITED_TIME_LUX_EXCLUSIVE),
        hideCount: hideCounts,
      })
    }
    return take(filteredSortedPopularItems, 10)
  }, [itemifyFilterGroup, popularFilters, isMapPage, localAvailableFilters?.filters?.holidayTypes, filters.holidayTypes, hideCounts, limitedTimeLuxExclusive])

  const scrollToTop = useCallback(() => {
    if (doNotScrollToTopOfSelection) return

    // Scroll the scrollable content and the page to to top
    scrollableContentRef.current?.scrollTo({
      top: 0,
      behavior: 'instant',
    })
    window.scrollTo({
      top: 0,
      behavior: 'instant' as any,
    })
  }, [doNotScrollToTopOfSelection])

  const onCampaignChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    const value = event.target.value
    const newSearchParams = toggleSearchParamValue(searchParams, queryKeyCampaigns, value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ campaigns: [value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onLuxPlusFeatureChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    const value = e.target.value
    if (value === LUX_PLUS_MONTH_CAMPAIGN_ID) {
      onCampaignChange(e)
      return
    }
    if (value.length && value.split(',').length === 1) {
      onFilterChange({ search: toggleSearchParamValue(searchParams, 'luxPlusFeatures', value), hash: currentHash })
      saveRecentFilters({ luxPlusFeatures: [value] })
    } else {
      onFilterChange({
        search: setManySearchParamValues(searchParams, [{ paramName: 'luxPlusFeatures', paramValue: value.length ? value : undefined }]),
        hash: currentHash,
      })
    }
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange, onCampaignChange])

  const onLoyaltyFeatureChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    const value = event.target.value
    if (value === triplePointCampaignId) {
      onCampaignChange(event)
      return
    }

    const newSearchParams = toggleSearchParamValue(searchParams, LOYALTY_SEARCH_FILTER_PARAM, value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ loyalty: [value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange, onCampaignChange])

  const onAgentHubFeatureChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const newSearchParams = toggleSearchParamValue(searchParams, 'agentHubFeatures', value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ agentHubFeatures: [value as App.AgentHubFeatureFilter] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onHolidayTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = toggleSearchParamValue(searchParams, 'holidayTypes', e.target.value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ holidayTypes: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onPropertyTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = toggleSearchParamValue(searchParams, 'propertyTypes', e.target.value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ propertyTypes: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onAmenityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = toggleSearchParamValue(searchParams, 'amenities', e.target.value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ amenities: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onPaymentOptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = toggleSearchParamValue(searchParams, 'paymentOptions', e.target.value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ paymentOptions: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onChainChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = toggleSearchParamValue(searchParams, 'chains', e.target.value, chainsDelimiter)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ chains: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onInclusionsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // historicalActions sync is handled by useEffect watching filters.inclusions
    const newSearchParams = toggleSearchParamValue(searchParams, 'inclusions', e.target.value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ inclusions: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onPropertyFacilityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = toggleSearchParamValue(searchParams, 'propertyFacilities', e.target.value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ propertyFacilities: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onRoomAmenityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = toggleSearchParamValue(searchParams, 'roomAmenities', e.target.value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ roomAmenities: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onBedTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = toggleSearchParamValue(searchParams, 'bedTypes', e.target.value)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    saveRecentFilters({ bedTypes: [e.target.value] })
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange])

  const onCustomerRatingChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (Number(e.target.value) === CUSTOMER_RATING_GTE.ANY_RATING.value) {
      onFilterChange({ search: deleteSearchParams(searchParams, 'customerRatingGte'), hash: currentHash })
    } else {
      onFilterChange({ search: setSearchParamValue(searchParams, 'customerRatingGte', e.target.value), hash: currentHash })
      saveRecentFilters({ customerRatingGte: Number(e.target.value) })
    }
    scrollToTop()
  }, [searchParams, currentHash, scrollToTop, onFilterChange, CUSTOMER_RATING_GTE])

  const onBedroomsChange = useCallback((value: number) => {
    onFilterChange({ search: updateRoomFilterSearch(searchParams, 'bedrooms', value), hash: currentHash })
    scrollToTop()
  }, [currentHash, searchParams, scrollToTop, onFilterChange])
  const debouncedBedroomsChange = useMemo(() => debounce(onBedroomsChange, DEBOUNCE_DELAY), [onBedroomsChange])

  const onBedroomsRadioChange = useCallback((value: number) => {
    onFilterChange({ search: updateBedroomsRadioFilterSearch(searchParams, value), hash: currentHash })
    scrollToTop()
  }, [currentHash, searchParams, scrollToTop, onFilterChange])

  const onBedsChange = useCallback((value: number) => {
    onFilterChange({ search: updateRoomFilterSearch(searchParams, 'beds', value), hash: currentHash })
    scrollToTop()
  }, [currentHash, searchParams, scrollToTop, onFilterChange])
  const debouncedBedsChange = useMemo(() => debounce(onBedsChange, DEBOUNCE_DELAY), [onBedsChange])

  const onBathroomsChange = useCallback((value: number) => {
    onFilterChange({ search: updateRoomFilterSearch(searchParams, 'bathrooms', value), hash: currentHash })
    scrollToTop()
  }, [currentHash, searchParams, scrollToTop, onFilterChange])
  const debouncedBathroomsChange = useMemo(() => debounce(onBathroomsChange, DEBOUNCE_DELAY), [onBathroomsChange])

  const onSortChange = useCallback((value: string) => {
    if (value === SORT_OPTION_RECOMMENDED.value) {
      onFilterChange({ search: deleteSearchParams(searchParams, 'sortBy'), hash: currentHash })
    } else {
      onFilterChange({ search: setSearchParamValue(searchParams, 'sortBy', value), hash: currentHash })
    }
    scrollToTop()
  }, [currentHash, searchParams, scrollToTop, onFilterChange, SORT_OPTION_RECOMMENDED])

  const onFilterGroupChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const hasSlugInFilter = (filterArray: Array<App.FilterItem> | undefined, slug: string) =>
      filterArray?.some(item => item.slug === slug) ?? false

    const deleteParamsSearchParams = deleteSearchParams(searchParams, ...['propertyName', 'propertyId'])
    onFilterChange({ search: deleteParamsSearchParams, hash: currentHash })

    let filterGroup: string | undefined
    if (isAgentHubExclusiveFilter(value)) {
      filterGroup = 'agentHubFeatures'
    } else if (hasSlugInFilter(localAvailableFilters?.filters?.holidayTypes, value) || filters.holidayTypes?.includes(value)) {
      filterGroup = 'holidayTypes'
    } else if (hasSlugInFilter(localAvailableFilters?.filters?.propertyTypes, value) || filters.propertyTypes?.includes(value)) {
      filterGroup = 'propertyTypes'
    } else if (hasSlugInFilter(localAvailableFilters?.filters?.amenities, value) || filters.amenities?.includes(value)) {
      filterGroup = 'amenities'
    } else if (hasSlugInFilter(localAvailableFilters?.filters?.propertyFacilities, value) || filters.propertyFacilities?.includes(value)) {
      filterGroup = 'propertyFacilities'
    } else if (hasSlugInFilter(localAvailableFilters?.filters?.roomAmenities, value) || filters.roomAmenities?.includes(value)) {
      filterGroup = 'roomAmenities'
    } else if (hasSlugInFilter(localAvailableFilters?.filters?.bedTypes, value) || filters.bedTypes?.includes(value)) {
      filterGroup = 'bedTypes'
    } else if (!isNaN(parseInt(value))) {
      filterGroup = 'customerRatingGte'
    } else if (filters.inclusions?.includes(value)) {
      filterGroup = 'inclusions'
    } else if (isOfTypeLuxPlusFeatureFilter(value)) {
      filterGroup = 'luxPlusFeatures'
    } else if (value === triplePointCampaignId || value === LUX_PLUS_MONTH_CAMPAIGN_ID) {
      filterGroup = queryKeyCampaigns
    } else if (isOfTypeLoyaltyFeatureFilter(value)) {
      filterGroup = LOYALTY_SEARCH_FILTER_PARAM
    } else if (hasSlugInFilter(localAvailableFilters?.filters?.campaigns, value) || filters.campaigns?.includes(value)) {
      filterGroup = queryKeyCampaigns
    } else if (hasSlugInFilter(localAvailableFilters?.filters?.paymentOptions, value) || filters.paymentOptions?.includes(value)) {
      filterGroup = 'paymentOptions'
    } else if (hasSlugInFilter(localAvailableFilters?.filters?.chains, value) || filters.chains?.includes(value)) {
      filterGroup = 'chains'
    } else if (hasSlugInFilter(localAvailableFilters?.filters?.inclusions, value)) {
      filterGroup = 'inclusions'
    }
    if (!filterGroup) return
    saveRecentFilters({ [filterGroup]: filterGroup === 'customerRatingGte' ? Number(value) : [value] })
    const delimiter = filterGroup === 'chains' ? chainsDelimiter : ','
    const newSearchParams = toggleSearchParamValue(searchParams, filterGroup, value, delimiter)
    onFilterChange({ search: newSearchParams, hash: currentHash })
    scrollToTop()
  }, [searchParams, localAvailableFilters?.filters, filters, currentHash, scrollToTop, onFilterChange])

  const onResetAllClick = useCallback(() => {
    const filtersToDelete = [...queryKeyFilters]
    const newSearchParams = deleteSearchParams(
      searchParams,
      ...filtersToDelete,
    )
    onFilterChange({ search: newSearchParams, hash: currentHash })
    scrollToTop()
  }, [currentHash, searchParams, scrollToTop, onFilterChange])

  const handleMapClick = useCallback(() => {
    trackClientEvent({
      subject: 'search_view_on_map',
      action: 'clicked',
      type: 'interaction',
      category: 'vertical_page',
    })
  }, [])

  const filterCount = useMemo(() => {
    return getAppliedFilterCount(filters, enabledFilters, hideCompareOffersToggle)
  }, [filters, enabledFilters, hideCompareOffersToggle])
  const hasFiltersApplied = filterCount > 0
  const resetAllLabel = intl.formatMessage(
    {
      id: 'SBUXnNFAtohL80iOyI2c',
      defaultMessage: 'Reset all ({filterCount})',
      description: 'Reset all filters link with active filter count on search page',
    },
    { filterCount },
  )

  const noAvailableFilters = localAvailableFilters && !localAvailableFilters.fetching && hasNoAvailableFilters(localAvailableFilters?.filters)
  const showRecentFilters = !isHomesVerticalSelected && isNonEmptyArray(recentFilterItems)
  const showPopularFilters = !isHomesVerticalSelected && enabledFilters.popularFilters && isNonEmptyArray(popularFilterItems) && !fetchingPopularFilters && !showRecentFilters

  const hasRoomBathOrBedFilters = useMemo(() => {
    return !!(enabledFilters.bedrooms || enabledFilters.bathrooms || enabledFilters.beds)
  }, [enabledFilters.bathrooms, enabledFilters.bedrooms, enabledFilters.beds])
  const shouldShowTopRoomsAndAmenities = !isHotelSearchResultsPage && (showRoomsAndAmenitiesAtTop || isHomesVerticalSelected) && hasRoomBathOrBedFilters && !bedroomsFilterGroupAtBottom
  const shouldShowAccommodationTypeFilter = !isHotelsVerticalSelected && enabledFilters.propertyTypes && isNonEmptyArray(accommodationTypeItems)
  const shouldShowAccommodationTypeNearTop = !isHotelSearchResultsPage && shouldShowAccommodationTypeFilter && (showAccommodationTypeAtTop || isHomesVerticalSelected)
  const shouldShowAmenitiesNearTop = !isHotelSearchResultsPage && (shouldShowTopRoomsAndAmenities || isHomesVerticalSelected) && enabledFilters.amenities && isNonEmptyArray(amenityItems)
  const shouldShowHolidayTypesFilter = enabledFilters.holidayTypes && isNonEmptyArray(holidayTypeItems)
  const shouldShowHomesHolidayTypesFilter = !isHotelSearchResultsPage && isHomesVerticalSelected && shouldShowHolidayTypesFilter
  const shouldShowStandardHolidayTypesFilter = !isHotelSearchResultsPage && !isHomesVerticalSelected && shouldShowHolidayTypesFilter
  const shouldShowInclusionsFilter = enabledFilters.inclusions && isNonEmptyArray(inclusionItems)
  const shouldShowHomesInclusionsFilter = !isHotelSearchResultsPage && isHomesVerticalSelected && shouldShowInclusionsFilter
  const shouldShowStandardInclusionsFilter = !isHotelSearchResultsPage && !isHomesVerticalSelected && shouldShowInclusionsFilter
  const shouldShowLoyaltyFilter = (enabledFilters.loyalty && loyaltyFeatureItems.length > 0) || loyaltyFeatureItems.some(item => item.value === triplePointCampaignId)
  const shouldShowAgentHubFeaturesFilter = enabledFilters.agentHubFeatures && agentHubFeatureItems.length > 0
  const shouldShowControlAmenities = !isHotelSearchResultsPage && enabledFilters.amenities && isNonEmptyArray(amenityItems)
  const shouldShowControlRoomsMid = !isHotelSearchResultsPage && hasRoomBathOrBedFilters && !bedroomsFilterGroupAtBottom && !shouldShowTopRoomsAndAmenities
  const shouldShowControlRoomsBottom = !isHotelSearchResultsPage && hasRoomBathOrBedFilters && bedroomsFilterGroupAtBottom
  const useFamilyFilterSectionOrder = isHotelSearchResultsPage && occupancyHasChildren(filters.rooms)
  const shouldShowTreatmentAgentHubAtTop = isHotelSearchResultsPage && shouldShowAgentHubFeaturesFilter
  const shouldShowControlAgentHub = !isHotelSearchResultsPage && shouldShowAgentHubFeaturesFilter
  const shouldShowControlLuxPlus = !isHotelSearchResultsPage && !!enabledFilters.luxPlusFeatures && luxPlusFeatureItems.length > 0
  const shouldShowControlSociete = !isHotelSearchResultsPage && !hideSocieteFilters
  const shouldShowControlCustomerRating = !isHotelSearchResultsPage && !!enabledFilters.customerRating && hasCustomerRatingOptions
  const shouldShowPropertyBrand = !isHomesVerticalSelected && enabledFilters.chains && isNonEmptyArray(chainItems)
  const shouldShowControlPropertyBrand = !isHotelSearchResultsPage && shouldShowPropertyBrand
  const shouldShowPaymentOptions = enabledFilters.paymentOptions && isNonEmptyArray(paymentOptionItems)
  const treatmentFilterSectionOrder = useFamilyFilterSectionOrder
    ? TREATMENT_FILTER_SECTION_ORDER_WITH_CHILDREN
    : TREATMENT_FILTER_SECTION_ORDER_DEFAULT

  const treatmentSections: Partial<Record<TreatmentFilterSection, React.ReactNode>> = isHotelSearchResultsPage ? {
    accommodationType: shouldShowAccommodationTypeFilter ? (
      <FilterPanelFilterGroup key="treatment-accommodation-type" loading={fetching} title={propertyTypeTitle}>
        <FilterPanelCheckboxGroup
          name="propertyTypes"
          items={accommodationTypeItems}
          onChange={onPropertyTypeChange}
          maxItems={5}
        />
      </FilterPanelFilterGroup>
    ) : null,
    inclusions: shouldShowInclusionsFilter ? (
      <FilterPanelFilterGroup key="treatment-inclusions" loading={fetching} title={inclusionsTitle}>
        <FilterPanelCheckboxGroup
          name="inclusions"
          items={inclusionItems}
          onChange={onInclusionsChange}
          maxItems={5}
        />
      </FilterPanelFilterGroup>
    ) : null,
    luxPlus: enabledFilters.luxPlusFeatures && luxPlusFeatureItems.length > 0 ? (
      <FilterPanelFilterGroup key="treatment-lux-plus" loading={fetching} title={<LuxPlusLabel type="only-logo" />}>
        <FilterPanelCheckboxGroup
          name="luxPlusFeatures"
          items={luxPlusFeatureItems}
          onChange={onLuxPlusFeatureChange}
          maxItems={5}
        />
      </FilterPanelFilterGroup>
    ) : null,
    societe: !hideSocieteFilters ? (
      <HotelSearchSocieteFilters
        key="treatment-societe"
        filters={filters}
        searchParams={searchParams}
        currentHash={currentHash}
        onFilterChange={onFilterChange}
        loading={fetching}
      >
        {shouldShowLoyaltyFilter && (
          <FilterPanelCheckboxGroup
            name="loyaltyFeatures"
            items={loyaltyFeatureItems}
            onChange={onLoyaltyFeatureChange}
            maxItems={5}
          />
        )}
      </HotelSearchSocieteFilters>
    ) : null,
    propertyFacilities: isNonEmptyArray(propertyFacilityItems) ? (
      <FilterPanelFilterGroup key="treatment-property-facilities" loading={fetching} title={propertyFacilitiesTitle}>
        <FilterPanelCheckboxGroup
          name="propertyFacilities"
          items={propertyFacilityItems}
          onChange={onPropertyFacilityChange}
          maxItems={5}
        />
      </FilterPanelFilterGroup>
    ) : null,
    roomAmenities: isNonEmptyArray(roomAmenityItems) ? (
      <FilterPanelFilterGroup key="treatment-room-amenities" loading={fetching} title={roomAmenitiesTitle}>
        <FilterPanelCheckboxGroup
          name="roomAmenities"
          items={roomAmenityItems}
          onChange={onRoomAmenityChange}
          maxItems={5}
        />
      </FilterPanelFilterGroup>
    ) : null,
    customerRating: enabledFilters.customerRating && hasCustomerRatingOptions ? (
      <FilterPanelFilterGroup key="treatment-customer-rating" loading={fetching} title={customerRatingTitle}>
        <FilterPanelRadioGroup
          name="customerRating"
          items={customerRatingItems}
          onChange={onCustomerRatingChange}
          selected={filters?.customerRatingGte ?? CUSTOMER_RATING_GTE.ANY_RATING.value}
          maxItems={5}
        />
      </FilterPanelFilterGroup>
    ) : null,
    holidayTypes: shouldShowHolidayTypesFilter ? (
      <FilterPanelFilterGroup key="treatment-holiday-types" loading={fetching} title={holidayTypeTitle}>
        <FilterPanelCheckboxGroup
          name="holidayTypes"
          items={holidayTypeItems}
          onChange={onHolidayTypeChange}
          maxItems={5}
        />
      </FilterPanelFilterGroup>
    ) : null,
    bedrooms: enabledFilters.bedrooms ? (
      <BedroomsRadioFilterGroup
        key="treatment-bedrooms"
        loading={fetching}
        bedroomsGte={filters.bedroomsGte}
        bedroomsEq={filters.bedroomsEq}
        useFamilyOptionOrder={useFamilyFilterSectionOrder}
        onChange={onBedroomsRadioChange}
      />
    ) : null,
    bedType: isNonEmptyArray(bedTypeItems) ? (
      <FilterPanelFilterGroup key="treatment-bed-type" loading={fetching} title={bedTypeTitle}>
        <FilterPanelCheckboxGroup
          name="bedTypes"
          items={bedTypeItems}
          onChange={onBedTypeChange}
          maxItems={3}
        />
      </FilterPanelFilterGroup>
    ) : null,
    propertyBrand: shouldShowPropertyBrand ? (
      <FilterPanelFilterGroup key="treatment-property-brand" loading={fetching} title={propertyBrandsTitle}>
        <FilterPanelCheckboxGroup
          name="chains"
          items={chainItems}
          onChange={onChainChange}
          maxItems={5}
        />
      </FilterPanelFilterGroup>
    ) : null,
  } : {}

  const isLuxuryEscapes = config.BRAND === 'luxuryescapes'

  return (
    <FilterPaneContainer $applyZoomCompensation={applyZoomCompensation}>
      <StickyHeader direction="vertical" gap={32}>
        {mapViewURL && <MapWindow mapViewURL={mapViewURL} onClick={handleMapClick} />}
        <CSSBreakpoint min="tablet">
          <Group direction="vertical" gap={24}>
            <Group direction="vertical" gap={8}>
              {isLuxuryEscapes && filters.destinationId && filters.destinationId !== ANYWHERE_PLACE_ID && isLoggedIn && userId && !hideAddDestinationToggle &&
                <StyledAddDestinationToggle
                  userId={userId}
                  placeId={filters.destinationId}
                />}
              {config.SEARCH_OFFER_COMPARISON_ENABLED && !hideCompareOffersToggle && <CompareOffersToggle />}
            </Group>
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
              <Heading variant="heading4">{filtersHeading}</Heading>
              {hasFiltersApplied && <TextLink onClick={onResetAllClick}>{resetAllLabel}</TextLink>}
            </Group>
          </Group>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
            <Heading variant="heading4">{sortAndFiltersHeading}</Heading>
            <IconButton kind="tertiary" variant="default" horizontalOutdent="end" onClick={onClose} aria-label={closeLabel}>
              <LineTimesIcon />
            </IconButton>
          </Group>
          <MobileComparisonToggleWrapper>
            {config.SEARCH_OFFER_COMPARISON_ENABLED && !hideCompareOffersToggle && <CompareOffersToggle />}
          </MobileComparisonToggleWrapper>
        </CSSBreakpoint>
      </StickyHeader>
      {sortOptions.length > 0 && <CSSBreakpoint max="mobile">
        <FilterPanelFilterGroup title={sortByTitle}>
          <InputGroup direction="vertical" gap={12}>
            {sortOptions.map(sortOption => <RadioInput
              key={`sortOption_${sortOption.value}`}
              name="sortBy"
              value={sortOption.value}
              onChange={() => onSortChange(sortOption.value)}
              checked={sortOption.value === (filters?.sortBy ?? SORT_OPTION_RECOMMENDED.value)}
            >
              {sortOption.label}
            </RadioInput>)}
          </InputGroup>
        </FilterPanelFilterGroup>
      </CSSBreakpoint>}
      {showLoadingSkeletons && <HotelSearchFilterPaneLoadingSkeleton />}
      <ScrollableContent ref={scrollableContentRef}>
        {(noAvailableFilters || showNoFiltersAvailable) && <StyledBodyText variant="medium">{noFiltersAvailableText}</StyledBodyText>}
        {showRecentFilters && <FilterPanelFilterGroup title={recentFiltersTitle}>
          <FilterPanelCheckboxGroup
            name="recentFilters"
            items={recentFilterItems}
            onChange={onFilterGroupChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {shouldShowAccommodationTypeNearTop && <FilterPanelFilterGroup loading={fetching} title={propertyTypeTitle}>
          <FilterPanelCheckboxGroup
            name="propertyTypes"
            items={accommodationTypeItems}
            onChange={onPropertyTypeChange}
            maxItems={4}
          />
        </FilterPanelFilterGroup>}
        {shouldShowTopRoomsAndAmenities && (
          <RoomFilterGroup
            loading={fetching}
            filters={filters}
            enabledFilters={enabledFilters}
            onBedroomsChange={debouncedBedroomsChange}
            onBedsChange={debouncedBedsChange}
            onBathroomsChange={debouncedBathroomsChange}
          />
        )}
        {shouldShowAmenitiesNearTop && <FilterPanelFilterGroup loading={fetching} title={amenitiesTitle}>
          <FilterPanelCheckboxGroup
            name="amenities"
            items={amenityItems}
            onChange={onAmenityChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {showPopularFilters && <FilterPanelFilterGroup title={popularFiltersTitle}>
          <FilterPanelCheckboxGroup
            name="popularFilters"
            items={popularFilterItems}
            onChange={onFilterGroupChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {shouldShowTreatmentAgentHubAtTop && <FilterPanelFilterGroup loading={fetching} title={agentHubFeaturesTitle}>
          <FilterPanelCheckboxGroup
            name="agentHubFeatures"
            items={agentHubFeatureItems}
            maxItems={5}
            onChange={onAgentHubFeatureChange}
          />
        </FilterPanelFilterGroup>}
        {isHotelSearchResultsPage && treatmentFilterSectionOrder.map((section) => treatmentSections[section])}
        {shouldShowControlLuxPlus && <FilterPanelFilterGroup loading={fetching} title={<LuxPlusLabel type="only-logo" />}>
          <FilterPanelCheckboxGroup
            name="luxPlusFeatures"
            items={luxPlusFeatureItems}
            onChange={onLuxPlusFeatureChange}
          />
        </FilterPanelFilterGroup>}
        {shouldShowHomesHolidayTypesFilter && <FilterPanelFilterGroup loading={fetching} title={holidayTypeTitle}>
          <FilterPanelCheckboxGroup
            name="holidayTypes"
            items={holidayTypeItems}
            onChange={onHolidayTypeChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {shouldShowHomesInclusionsFilter &&
          <FilterPanelFilterGroup loading={fetching} title={inclusionsTitle}>
            <FilterPanelCheckboxGroup
              name="inclusions"
              items={inclusionItems}
              onChange={onInclusionsChange}
              maxItems={4}
            />
          </FilterPanelFilterGroup>}
        {shouldShowControlSociete && <HotelSearchSocieteFilters
          filters={filters}
          searchParams={searchParams}
          currentHash={currentHash}
          onFilterChange={onFilterChange}
          loading={fetching}
        >
          {shouldShowLoyaltyFilter && (
            <FilterPanelCheckboxGroup
              name="loyaltyFeatures"
              items={loyaltyFeatureItems}
              onChange={onLoyaltyFeatureChange}
            />
          )}
        </HotelSearchSocieteFilters>}
        {shouldShowControlAgentHub && <FilterPanelFilterGroup loading={fetching} title={agentHubFeaturesTitle}>
          <FilterPanelCheckboxGroup
            name="agentHubFeatures"
            items={agentHubFeatureItems}
            maxItems={5}
            onChange={onAgentHubFeatureChange}
          />
        </FilterPanelFilterGroup>}
        {shouldShowStandardInclusionsFilter &&
          <FilterPanelFilterGroup loading={fetching} title={inclusionsTitle}>
            <FilterPanelCheckboxGroup
              name="inclusions"
              items={inclusionItems}
              onChange={onInclusionsChange}
              maxItems={4}
            />
          </FilterPanelFilterGroup>}
        {shouldShowControlCustomerRating && <FilterPanelFilterGroup loading={fetching} title={customerRatingTitle}>
          <FilterPanelRadioGroup
            name="customerRating"
            items={customerRatingItems}
            onChange={onCustomerRatingChange}
            selected={filters?.customerRatingGte ?? CUSTOMER_RATING_GTE.ANY_RATING.value}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {shouldShowStandardHolidayTypesFilter && <FilterPanelFilterGroup loading={fetching} title={holidayTypeTitle}>
          <FilterPanelCheckboxGroup
            name="holidayTypes"
            items={holidayTypeItems}
            onChange={onHolidayTypeChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {!shouldShowAccommodationTypeNearTop && !isHotelSearchResultsPage && shouldShowAccommodationTypeFilter && <FilterPanelFilterGroup loading={fetching} title={propertyTypeTitle}>
          <FilterPanelCheckboxGroup
            name="propertyTypes"
            items={accommodationTypeItems}
            onChange={onPropertyTypeChange}
            maxItems={4}
          />
        </FilterPanelFilterGroup>}
        {!shouldShowAmenitiesNearTop && shouldShowControlAmenities && <FilterPanelFilterGroup loading={fetching} title={amenitiesTitle}>
          <FilterPanelCheckboxGroup
            name="amenities"
            items={amenityItems}
            onChange={onAmenityChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {shouldShowPaymentOptions && <FilterPanelFilterGroup loading={fetching} title={paymentOptionsTitle}>
          <FilterPanelCheckboxGroup
            name="paymentOptions"
            items={paymentOptionItems}
            onChange={onPaymentOptionChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {shouldShowControlPropertyBrand && <FilterPanelFilterGroup loading={fetching} title={propertyBrandsTitle}>
          <FilterPanelCheckboxGroup
            name="chains"
            items={chainItems}
            onChange={onChainChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
        {shouldShowControlRoomsMid && (
          <RoomFilterGroup
            loading={fetching}
            filters={filters}
            enabledFilters={enabledFilters}
            onBedroomsChange={debouncedBedroomsChange}
            onBedsChange={debouncedBedsChange}
            onBathroomsChange={debouncedBathroomsChange}
          />
        )}
        {shouldShowControlRoomsBottom && (
          <RoomFilterGroup
            loading={fetching}
            filters={filters}
            enabledFilters={enabledFilters}
            onBedroomsChange={debouncedBedroomsChange}
            onBedsChange={debouncedBedsChange}
            onBathroomsChange={debouncedBathroomsChange}
          />
        )}
      </ScrollableContent>
    </FilterPaneContainer>
  ) }

export default connect<MappedStateProps, undefined, Props, App.State>(
  (appState): MappedStateProps => {
    return {
      popularFilters: appState.destination.popularFilters,
      fetchingPopularFilters: appState.destination.fetchingPopularFilters,
      pathname: appState.router.location.pathname,
      currentHash: appState.router.location.hash,
      canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(appState),
      userId: appState.auth.account.memberId,
      isLoggedIn: selectLoggedIn(appState),
    }
  },
)(HotelSearchPageFilterPane)
